import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMemo, useEffect, useState } from "react";
import { startOfMonth, endOfMonth, format } from "date-fns";

export const ActivityChartSkeleton = () => {
  const isMobile = useIsMobile();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Add window resize listener
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate days in current month
  const daysInMonth = useMemo(() => {
    const today = new Date();
    const firstDay = startOfMonth(today);
    const lastDay = endOfMonth(today);
    return lastDay.getDate(); // Number of days in the month
  }, []);

  // Determine layout based on screen size and month length
  const layout = useMemo(() => {
    if (isMobile) {
      return { rows: 3, cols: Math.ceil(daysInMonth / 3) };
    } else {
      if (windowWidth >= 1200) {
        return { rows: 1, cols: daysInMonth }; // Single row for large screens
      } else {
        return { rows: 2, cols: Math.ceil(daysInMonth / 2) }; // Two rows for medium screens
      }
    }
  }, [isMobile, windowWidth, daysInMonth]);

  // Group days into rows based on layout
  const rows = useMemo(() => {
    const result = [];
    const { rows: numRows } = layout;

    // Calculate days per row, ensuring all days fit
    const daysPerRow = Math.ceil(daysInMonth / numRows);

    for (let i = 0; i < numRows; i++) {
      const daysInThisRow = Math.min(daysPerRow, daysInMonth - i * daysPerRow);
      result.push(daysInThisRow);
    }

    return result;
  }, [layout, daysInMonth]);

  // Get current month name
  const currentMonth = format(new Date(), 'MMMM');

  return (
    <Card className="border border-stone-200 dark:border-stone-700 shadow-sm dark:bg-stone-800">
      <CardHeader className="px-8">
        <CardTitle className="text-xl dark:text-white">Your activity this {currentMonth}</CardTitle>
      </CardHeader>
      <CardContent className="px-8 pb-6">
        <div className="space-y-6">
          <div className="flex flex-col space-y-6">
            {rows.map((daysInRow, rowIndex) => (
              <div key={rowIndex} className="flex justify-between">
                {[...Array(daysInRow)].map((_, colIndex) => (
                  <Skeleton key={colIndex} className="w-6 h-6 rounded-sm" />
                ))}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};